import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@cakrawalajakarta/shared-components";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";

const FUNRUN_EVENT = {
  eventId: "FUNRUN5K2026",
  slug: "funrun5k",
  title: "Fun Run 5K Cibodas",
  collectionName: "pendaftaranFunRun5K2026",
  category: {
    categoryName: "FUN RUN 5K CIBODAS",
    distance: 5,
    price: 250000,
    foreignerPrice: 250000,
  },
};

const routePoints = [
  { km: "0 KM", titik: "Start Line", ket: "Gerbang Utama Kebun Raya Cibodas" },
  { km: "1 KM", titik: "Zona Kebun Botani", ket: "Koleksi tanaman tropis dan spot foto jalur hijau" },
  { km: "2 KM", titik: "Air Terjun Cismun", ket: "Titik highlight rute dengan view air terjun" },
  { km: "3 KM", titik: "Jalur Hutan", ket: "Lintasan teduh dengan panorama Gunung Gede-Pangrango" },
  { km: "4 KM", titik: "Taman Bunga", ket: "Area terbuka dan zona hiburan peserta" },
  { km: "5 KM", titik: "Finish Line", ket: "Kembali ke gerbang utama dan area selebrasi" },
];

const rundown = [
  ["05.30", "Persiapan panitia & venue"],
  ["06.00", "Gate peserta dibuka + sesi pemanasan"],
  ["06.30", "Flag-off Fun Run 5K"],
  ["07.00", "Rute berjalan + water station aktif"],
  ["08.00 - 09.30", "Medali & sertifikat di garis finish"],
  ["10.00", "Pengundian doorprize"],
  ["11.30", "Event selesai"],
];

export const FunRun5K = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const handleRegister = () => {
    if (!user) {
      showNotification("info", "Please login first to register.");
      navigate("/login");
      return;
    }

    navigate(`/form/${FUNRUN_EVENT.slug}`, {
      state: {
        title: `${FUNRUN_EVENT.title} - ${FUNRUN_EVENT.category.categoryName}`,
        price: FUNRUN_EVENT.category.price,
        foreignerPrice: FUNRUN_EVENT.category.foreignerPrice,
        eventId: FUNRUN_EVENT.eventId,
        categoryName: FUNRUN_EVENT.category.categoryName,
        collectionName: FUNRUN_EVENT.collectionName,
      },
    });
  };

  return (
    <div className="w-full bg-white text-slate-900">
      <section
        className="relative -mt-20 min-h-[72vh] pt-28 md:pt-36 bg-cover bg-center"
        style={{ backgroundImage: "url('/bg/bghero11.jpg')" }}
      >
        <div className="absolute inset-0 bg-lime-600/55" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 container mx-auto px-4 py-16 md:py-24">
          <motion.p
            className="text-white uppercase font-semibold tracking-wide text-sm md:text-base"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            viewport={{ once: true }}
          >
            JOTR CIANJUR SERIES 2026
          </motion.p>
          <motion.h1
            className="text-white font-black uppercase text-4xl md:text-6xl leading-tight mt-3"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.06 }}
            viewport={{ once: true }}
          >
            Fun Run 5K Cibodas
          </motion.h1>
          <motion.p
            className="text-white text-lg md:text-2xl mt-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            viewport={{ once: true }}
          >
            Lari Segar, Alam Lestari
          </motion.p>
          <motion.div
            className="mt-8 flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.18 }}
            viewport={{ once: true }}
          >
            <Button onClick={handleRegister}>
              Daftar Sekarang
            </Button>
            <Button
              variant="secondary"
              className="text-slate-900"
              onClick={() => {
                const el = document.getElementById("detail-event");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Lihat Detail
            </Button>
            <Button
              variant="secondary"
              className="text-slate-900"
              onClick={() => navigate("/funrun5k/entrylist")}
            >
              Entry List
            </Button>
          </motion.div>
        </div>
      </section>

      <section id="detail-event" className="container mx-auto px-4 py-14">
        <h2 className="text-3xl md:text-4xl font-black text-lime-600 uppercase">Latar Belakang</h2>
        <p className="mt-4 text-lg leading-relaxed text-slate-700 max-w-5xl">
          Fun Run 5K Cibodas hadir untuk mendorong gaya hidup aktif masyarakat Cianjur melalui pengalaman lari alam terbuka yang inklusif, meriah, dan mudah diikuti semua kalangan.
        </p>
        <p className="mt-4 text-lg leading-relaxed text-slate-700 max-w-5xl">
          Venue Kebun Raya Cibodas dipilih karena jalur sejuk pegunungan, lanskap hutan tropis, dan panorama ikonik Air Terjun Cibodas yang memberi pengalaman lari berbeda dari event kota pada umumnya.
        </p>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4 py-14">
          <h2 className="text-3xl md:text-4xl font-black text-lime-600 uppercase">Rute Fun Run 5K</h2>
          <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left">
              <thead className="bg-lime-600 text-white">
                <tr>
                  <th className="px-4 py-3">KM</th>
                  <th className="px-4 py-3">Titik</th>
                  <th className="px-4 py-3">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {routePoints.map((row) => (
                  <tr key={`${row.km}-${row.titik}`} className="border-t border-slate-200">
                    <td className="px-4 py-3 font-bold text-lime-600 whitespace-nowrap">{row.km}</td>
                    <td className="px-4 py-3 font-semibold">{row.titik}</td>
                    <td className="px-4 py-3 text-slate-700">{row.ket}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-14">
        <h2 className="text-3xl md:text-4xl font-black text-lime-600 uppercase">Rundown Hari-H</h2>
        <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left">
            <thead className="bg-lime-600 text-white">
              <tr>
                <th className="px-4 py-3">Waktu</th>
                <th className="px-4 py-3">Kegiatan</th>
              </tr>
            </thead>
            <tbody>
              {rundown.map(([time, activity]) => (
                <tr key={`${time}-${activity}`} className="border-t border-slate-200">
                  <td className="px-4 py-3 font-semibold text-lime-600 whitespace-nowrap">{time}</td>
                  <td className="px-4 py-3 text-slate-700">{activity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
